// components/AdmissionsModal.tsx
"use client";

import { useRouter } from "next/navigation";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { motion } from "motion/react";
import Image from "next/image";
import { Calendar, Star } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useEffect } from "react";

const AdmissionsModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const router = useRouter();
  const posthog = usePostHog();

  useEffect(() => {
    if (isOpen) {
      posthog.capture("admissions_modal_opened");
    }
  }, [isOpen, posthog]);

  const handleRegisterClick = () => {
    posthog.capture("admissions_apply_clicked");
    onClose();
    router.push("/online-admission");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[92vw] sm:max-w-[400px] h-auto max-h-[90vh] overflow-y-auto mx-auto p-0 rounded-2xl border border-border shadow-2xl bg-card">
        <div className="p-6 sm:p-8 flex flex-col items-center text-center relative">
          {/* Subtle top decoration */}
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary/60 via-primary to-primary/60"></div>

          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="w-12 h-12 bg-slate-300 rounded-full flex items-center justify-center mb-5 mt-2 shadow-sm border border-primary/20"
          >
            <Image
              priority
              quality={100}
              src="/logoschool.png"
              alt="Admissions"
              width={50}
              height={50}
              className="object-contain"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="space-y-2 mb-6"
          >
            <div className="inline-block px-3 py-1 bg-primary/10 text-primary rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-1">
              Academic Year 2083
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight">
              Admissions Open
            </h2>
            <p className="text-muted-foreground text-sm leading-relaxed px-2">
              Join our prestigious institution and build your child&apos;s
              future. Seats are limited for the upcoming session.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="w-full bg-muted/40 rounded-xl p-4 mb-7 space-y-3 text-left border border-border/50"
          >
            <div className="flex items-center text-foreground">
              <div className="bg-background rounded-md p-1.5 border border-border/50 mr-3 shadow-sm">
                <Calendar className="h-4 w-4 text-primary flex-shrink-0" />
              </div>
              <span className="text-sm font-medium">
                Deadline:{" "}
                <span className="font-bold text-primary">Baisakh 15, 2083</span>
              </span>
            </div>

            <div className="flex items-center text-foreground">
              <div className="bg-background rounded-md p-1.5 border border-border/50 mr-3 shadow-sm">
                <Star className="h-4 w-4 text-primary flex-shrink-0" />
              </div>
              <span className="text-sm font-medium leading-tight">
                Early applicants receive priority
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="w-full"
          >
            <Button
              size="lg"
              onClick={handleRegisterClick}
              className="w-full rounded-xl text-base font-semibold shadow-md active:scale-[0.98] transition-all"
            >
              Apply Now
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            className="mt-6 flex flex-col space-y-1 text-center"
          >
            <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">
              Need help joining?
            </span>
            <div className="flex items-center justify-center gap-2 text-sm mt-1">
              <a
                href="mailto:admin@janakalyanboardingschool.edu.np"
                className="text-primary font-medium hover:underline"
              >
                Email Us
              </a>
              <span className="text-muted-foreground/50">•</span>
              <a
                href="tel:+9779808718289"
                className="text-primary font-medium hover:underline"
              >
                Call Us
              </a>
            </div>
          </motion.div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdmissionsModal;
